import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  CircularProgress,
  OutlinedInput,
  Checkbox,
  ListItemText,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Stack,
  Typography,
  Autocomplete,
} from "@mui/material";
import axios from "../../../config/axiosConfig";
import { getTokenData } from "../../../utils/authUtils";

const EMPTY_FORM = {
  tipo_ot: "",
  observacion: "",
  num_recibo: "",
  fecha_termino: "",
  nom_tecnico: [],
};

// AVL/Scada solo tiene habilitadas "Venta de Equipo", "Venta de Equipos Activos", "Adicion de Chips" y "Devolucion de Chips" — el
// resto de tipos (Adición, Mantenimiento, Reemplazo, Devolución, etc.) no tienen
// pantalla propia para AVL en este flujo.
// Los reemplazos de equipos vendidos solo los crean estos roles (el backend lo valida también).
const TIPOS_OT_SOLO_GERENCIA = ["Remplazo Equipos Vendidos Activos", "Remplazo Equipos Vendidos Inactivos"];
const ROLES_GERENCIA_OT = ["admin", "gerente", "gerente_cbba"];
const TIPOS_OT_HABILITADOS_AVL = ["Venta de Equipo", "Venta de Equipos Activos", "Adicion de Chips", "Devolucion de Chips"];
// Solo se ofrecen a clientes AVL/Scada (es decir, desde Requerimientos): un cliente de otro servicio nunca ve estos tipos.
const TIPOS_OT_SOLO_AVL = ["Adicion de Chips", "Devolucion de Chips"];

const FormularioOT = ({ cliente, open, onClose }) => {
  const navigate = useNavigate();
  const [tiposOT, setTiposOT] = useState([]);
  const [tecnicos, setTecnicos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showError, setShowError] = useState("");
  const [form, setForm] = useState(EMPTY_FORM);

  // Solo aplica cuando el tipo elegido es "Devolucion" — decide de una vez si esta
  // OT es una transferencia a otro cliente, para que DevolucionEquiposOT.jsx no
  // tenga que ofrecer ese toggle a mitad de camino (ver plan: evita mezclar
  // equipos entre los dos modos).
  const [esTransferencia, setEsTransferencia] = useState(false);
  const [clienteDestino, setClienteDestino] = useState(null);
  const [buscarDestinoInput, setBuscarDestinoInput] = useState("");
  const [opcionesDestino, setOpcionesDestino] = useState([]);
  const [buscandoDestino, setBuscandoDestino] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm(EMPTY_FORM);
    setShowError("");
    setEsTransferencia(false);
    setClienteDestino(null);
    setBuscarDestinoInput("");
    setLoading(true);

    const tokenData = getTokenData();
    const sucursal = tokenData?.sucursal || "";

    Promise.all([
      axios.get("/api/ot-tipos/", { params: { soloActivos: 1 } }),
      axios.get("/api/usuarios-trs", { params: { role: "tecnico", sucursal } }),
    ])
      .then(([resTipos, resTecnicos]) => {
        setTiposOT(resTipos.data || []);
        setTecnicos(resTecnicos.data || []);
      })
      .catch((err) => console.error("Error al cargar datos", err))
      .finally(() => setLoading(false));
  }, [open]);

  // "Mantenimiento Traspaso" es exclusivo de Cochabamba (así lo describe el propio
  // catálogo ot_tipo) — se oculta del selector para cualquier otra sucursal. Por
  // nombre, no por id fijo, mismo criterio que esTipoDevolucion más abajo.
  const tiposOrdenados = useMemo(() => {
    const sucursal = getTokenData()?.sucursal || "";
    const esGerencia = ROLES_GERENCIA_OT.includes(getTokenData()?.role);
    const esAVL = cliente?.nombre_servicio === "AVL" || cliente?.nombre_servicio === "Scada";
    return [...tiposOT]
      .filter((t) => t.nombre !== "Mantenimiento Traspaso" || sucursal === "Cochabamba")
      .filter((t) => esGerencia || !TIPOS_OT_SOLO_GERENCIA.includes(t.nombre))
      .filter((t) => !esAVL || TIPOS_OT_HABILITADOS_AVL.includes(t.nombre))
      .filter((t) => esAVL || !TIPOS_OT_SOLO_AVL.includes(t.nombre))
      .sort((a, b) => String(a.nombre).localeCompare(String(b.nombre)));
  }, [tiposOT, cliente?.nombre_servicio]);

  // Por nombre, no por id fijo — más robusto ante un futuro reseed de ot_tipo.
  const esTipoDevolucion = useMemo(() => {
    const tipo = tiposOT.find((t) => String(t.id) === String(form.tipo_ot));
    return tipo?.nombre === "Devolucion";
  }, [tiposOT, form.tipo_ot]);

  useEffect(() => {
    if (!esTipoDevolucion) {
      setEsTransferencia(false);
      setClienteDestino(null);
    }
  }, [esTipoDevolucion]);

  // Mismo patrón de búsqueda que ya usaba DevolucionEquiposOT.jsx para elegir el
  // cliente destino — se traslada acá tal cual.
  useEffect(() => {
    if (!esTransferencia) return undefined;
    const texto = clienteDestino ? "" : (buscarDestinoInput || "").trim();
    if (!texto) {
      setOpcionesDestino([]);
      return undefined;
    }
    let activo = true;
    const timer = setTimeout(() => {
      setBuscandoDestino(true);
      axios
        .get("/api/req-solicitudes/catalogo-clientes-servicio", {
          params: {
            buscar: texto,
            page: 1,
            pageSize: 20,
            cod_servicio: cliente?.nombre_servicio || undefined,
            excluir_servicio: "AVL,Scada",
          },
        })
        .then((res) => {
          const opciones = (res?.data?.items || res?.data || []).filter(
            (o) => String(o.cod_servicio_cliente) !== String(cliente?.cod_servicio_cliente)
          );
          if (activo) setOpcionesDestino(opciones);
        })
        .catch(() => {
          if (activo) setOpcionesDestino([]);
        })
        .finally(() => {
          if (activo) setBuscandoDestino(false);
        });
    }, 300);
    return () => {
      activo = false;
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [esTransferencia, buscarDestinoInput, clienteDestino]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.tipo_ot) {
      setShowError("Selecciona el tipo de Orden de Trabajo.");
      return;
    }
    if (esTransferencia && !clienteDestino) {
      setShowError("Selecciona el cliente destino de la transferencia.");
      return;
    }

    setSubmitting(true);
    setShowError("");

    const payload = {
      id_servicio_cliente: cliente.cod_servicio_cliente,
      tipo_servicio: cliente.nombre_servicio,
      tipo_ot: form.tipo_ot,
      observacion: form.observacion,
      num_recibo: form.num_recibo,
      fecha_termino: form.fecha_termino || null,
      nom_tecnico: form.nom_tecnico.join(", "),
      servicio_destino: esTransferencia ? clienteDestino?.cod_servicio_cliente : undefined,
    };

    try {
      const { data: solicitud } = await axios.post("/api/ot-solicitudes/", payload);
      onClose();
      navigate(`/orden-de-trabajo/ot/${solicitud.id}`);
    } catch (err) {
      console.error("Error al guardar la orden", err);
      setShowError(err?.response?.data?.error || "Ocurrió un error al guardar la orden.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        Nueva Orden de Trabajo
        <Typography variant="body2" color="text.secondary">
          {cliente?.nombre_cliente} — {cliente?.descripcion || cliente?.nombre} ({cliente?.nombre_servicio})
        </Typography>
      </DialogTitle>
      <DialogContent>
        {loading ? (
          <Stack alignItems="center" sx={{ py: 4 }}>
            <CircularProgress />
          </Stack>
        ) : (
          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
            {showError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {showError}
              </Alert>
            )}

            <FormControl fullWidth sx={{ mb: 2 }} required>
              <InputLabel>Tipo de Orden de Trabajo</InputLabel>
              <Select
                name="tipo_ot"
                value={form.tipo_ot}
                onChange={handleChange}
                label="Tipo de Orden de Trabajo"
              >
                {tiposOrdenados.map((tipo) => (
                  <MenuItem key={tipo.id} value={tipo.id}>{tipo.nombre}</MenuItem>
                ))}
              </Select>
            </FormControl>

            {esTipoDevolucion && (
              <>
                <FormControlLabel
                  sx={{ mb: 1 }}
                  control={
                    <Checkbox
                      checked={esTransferencia}
                      onChange={(e) => {
                        setEsTransferencia(e.target.checked);
                        setClienteDestino(null);
                        setBuscarDestinoInput("");
                      }}
                    />
                  }
                  label="¿Es transferencia a otro cliente?"
                />

                {esTransferencia && (
                  <Autocomplete
                    fullWidth
                    options={opcionesDestino}
                    loading={buscandoDestino}
                    value={clienteDestino}
                    onChange={(e, value) => setClienteDestino(value)}
                    inputValue={buscarDestinoInput}
                    onInputChange={(e, value) => setBuscarDestinoInput(value)}
                    getOptionLabel={(o) =>
                      o ? `${o.razon_social || ""} — ${o.descripcion || ""} (#${o.cod_servicio_cliente})` : ""
                    }
                    isOptionEqualToValue={(a, b) => a.cod_servicio_cliente === b.cod_servicio_cliente}
                    noOptionsText="Sin resultados"
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Cliente Destino"
                        placeholder="Buscar cliente..."
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {buscandoDestino ? <CircularProgress size={18} /> : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                    sx={{ mb: 2 }}
                  />
                )}
              </>
            )}

            <TextField
              fullWidth
              multiline
              rows={3}
              name="observacion"
              label="Observaciones"
              value={form.observacion}
              onChange={handleChange}
              sx={{ mb: 2 }}
            />

            <TextField
              fullWidth
              name="num_recibo"
              label="Num. Recibo"
              value={form.num_recibo}
              onChange={handleChange}
              sx={{ mb: 2 }}
            />

            <TextField
              fullWidth
              type="date"
              name="fecha_termino"
              label="Fecha Culminación"
              value={form.fecha_termino}
              onChange={handleChange}
              InputLabelProps={{ shrink: true }}
              sx={{ mb: 2 }}
            />

            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Técnicos</InputLabel>
              <Select
                multiple
                name="nom_tecnico"
                value={form.nom_tecnico}
                onChange={handleChange}
                input={<OutlinedInput label="Técnicos" />}
                renderValue={(selected) => selected.join(", ")}
              >
                {tecnicos.map((tecnico) => (
                  <MenuItem key={tecnico.id} value={tecnico.nombre}>
                    <Checkbox checked={form.nom_tecnico.indexOf(tecnico.nombre) > -1} />
                    <ListItemText primary={tecnico.nombre} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <DialogActions sx={{ px: 0 }}>
              <Button onClick={onClose} disabled={submitting}>Cancelar</Button>
              <Button variant="contained" color="primary" type="submit" disabled={submitting}>
                {submitting ? <CircularProgress size={20} /> : "Guardar"}
              </Button>
            </DialogActions>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default FormularioOT;
